import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import type { UnknownAction } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';
import { SinatraState } from '@rsos/sinatra';
import { notifyError } from '@rsos/utils/toastUtils';
import {
  fetchKeywordTiers,
  keywordTiersSelector,
  KeywordTiersState,
  updateKeywordTiers,
} from './keywordTiersSlice';

export type KeywordTier = {
  id: 'tier-1' | 'tier-2' | 'tier-3';
  name: 'Tier 1' | 'Tier 2' | 'Tier 3';
  notifications: boolean;
};

export type KeywordTierMap = {
  'tier-1': KeywordTier;
  'tier-2': KeywordTier;
  'tier-3': KeywordTier;
};

const nameForTierId = (tierId: KeywordTier['id']) => {
  return { 'tier-1': 'Tier 1', 'tier-2': 'Tier 2', 'tier-3': 'Tier 3' }[
    tierId
  ] as KeywordTier['name'];
};

const initialTiers: KeywordTier[] = [
  { id: 'tier-1', name: 'Tier 1', notifications: true },
  { id: 'tier-2', name: 'Tier 2', notifications: true },
  { id: 'tier-3', name: 'Tier 3', notifications: true },
];

const disabledTiers: KeywordTier[] = [
  { id: 'tier-1', name: 'Tier 1', notifications: false },
  { id: 'tier-2', name: 'Tier 2', notifications: false },
  { id: 'tier-3', name: 'Tier 3', notifications: false },
];

type KeywordTierDispatch = ThunkDispatch<
  { keywordTiers: KeywordTiersState; sinatra: SinatraState },
  string,
  UnknownAction
>;

export const useKeywordTiers = () => {
  const dispatch: KeywordTierDispatch = useDispatch();
  const { keywordTiers, loading, loadingError } =
    useSelector(keywordTiersSelector);
  useEffect(() => {
    if (!keywordTiers && !loading && !loadingError) {
      dispatch(fetchKeywordTiers());
    }
  }, [dispatch, keywordTiers, loading, loadingError]);

  if (loading) {
    return { tiers: [], tierMap: null, error: loadingError };
  }
  const tiers =
    keywordTiers && keywordTiers.length ? keywordTiers : initialTiers;
  const tierMap = Object.fromEntries(
    tiers.map(t => [t.id, t]),
  ) as KeywordTierMap;

  return { tiers, tierMap, error: loadingError };
};

export const useKeywordTierActions = () => {
  const dispatch: KeywordTierDispatch = useDispatch();
  const { tiers } = useKeywordTiers();

  return {
    update(tierId: KeywordTier['id'], notifications: boolean) {
      const otherTiers = tiers.filter(t => t.id !== tierId);
      return dispatch(
        updateKeywordTiers([
          ...otherTiers,
          { id: tierId, name: nameForTierId(tierId), notifications },
        ]),
      )
        .unwrap()
        .catch(() => {
          notifyError('Failed to save keyword notification settings.');
        });
    },
    disable() {
      return dispatch(updateKeywordTiers(disabledTiers))
        .unwrap()
        .catch(() => {
          notifyError('Failed to save keyword notification settings.');
        });
    },
    enable() {
      return dispatch(updateKeywordTiers(initialTiers))
        .unwrap()
        .catch(() => {
          notifyError('Failed to save keyword notification settings.');
        });
    },
    refetch() {
      return dispatch(fetchKeywordTiers());
    },
  };
};
